import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase/client';
import { useToast } from './useToast';
import { Department } from '@/types/departments';

export function useDepartments() {
  const [departments, setDepartments] = useState<Department[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchDepartments = async (includeInactive = false) => {
    setIsLoading(true);
    setError(null);

    try {
      let query = supabase
        .from('departments')
        .select('*, manager:manager_id(id, full_name, email, role)')
        .is('deleted_at', null);

      if (!includeInactive) {
        query = query.eq('status', 'active');
      }

      const { data, error } = await query.order('name');

      if (error) throw error;

      setDepartments(data || []);
    } catch (err) {
      console.error('Error fetching departments:', err);
      setError('Failed to load departments');
      toast({
        title: 'Error',
        description: 'Failed to load departments',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getDepartment = async (id: string): Promise<Department | null> => {
    try {
      const { data, error } = await supabase
        .from('departments')
        .select('*, manager:manager_id(id, full_name, email, role)')
        .eq('id', id)
        .is('deleted_at', null)
        .single();

      if (error) throw error;

      return data;
    } catch (err) {
      console.error('Error fetching department:', err);
      return null;
    }
  };

  const updateDepartmentStatus = async (id: string, status: 'active' | 'inactive' | 'archived') => {
    try {
      const { error } = await supabase
        .from('departments')
        .update({ status, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;

      await fetchDepartments();
      
      toast({
        title: 'Status Updated',
        description: `Department status changed to ${status}`,
      });
      
      return true;
    } catch (err) {
      console.error('Error updating department status:', err);
      toast({
        title: 'Error',
        description: 'Failed to update department status',
        variant: 'destructive'
      });
      return false;
    }
  };

  const softDeleteDepartment = async (id: string) => {
    try {
      const { error } = await supabase
        .from('departments')
        .update({ 
          deleted_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (error) throw error;

      await fetchDepartments();
      
      toast({
        title: 'Department Deleted',
        description: 'Department was successfully deleted',
      });
      
      return true;
    } catch (err) {
      console.error('Error deleting department:', err);
      toast({
        title: 'Error',
        description: 'Failed to delete department',
        variant: 'destructive'
      });
      return false;
    }
  };

  const restoreDepartment = async (id: string) => {
    try {
      const { error } = await supabase
        .from('departments')
        .update({ 
          deleted_at: null,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (error) throw error;

      await fetchDepartments();
      
      toast({
        title: 'Department Restored',
        description: 'Department was successfully restored',
      });
      
      return true;
    } catch (err) {
      console.error('Error restoring department:', err);
      toast({
        title: 'Error',
        description: 'Failed to restore department',
        variant: 'destructive'
      });
      return false;
    }
  };

  useEffect(() => {
    fetchDepartments();
  }, []);

  return {
    departments,
    isLoading,
    error,
    fetchDepartments,
    getDepartment,
    updateDepartmentStatus,
    softDeleteDepartment,
    restoreDepartment
  };
}
